import { FC, useEffect, useState } from "react";

import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Fade from "@mui/material/Fade";
import List from "@mui/material/List";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";

import { UserSettingsInput } from "../../../../../../API";
import FullPageLoader from "../../../../../../common/components/item/FullPageLoader";
import { useUpdateUserSettings } from "../../../../../../common/hooks/useUpdateUserSettings";
import {
  errorNotification,
  successNotification,
} from "../../../../../../common/variables/notification";
import {
  DEFAULT_CONTROL_ROOM_REFRESH_MINUTES,
  IParsedUserInfo,
} from "../../../../../../leak-finder/hooks/useLazyGetUser";
import UserPreferencesSection from "../UserPreferencesSection";
import ControlRoomRefreshOption from "./ControlRoomRefreshOption";

export interface IRefreshOption {
  value: number;
  label: string;
}

type ExtendedUserSettingsInput = UserSettingsInput & {
  controlRoomRefreshMinutes?: number | null;
};

export const refreshOptions: IRefreshOption[] = [
  {
    value: 0,
    label: "Disabled",
  },
  {
    value: 1,
    label: "1 minute",
  },
  {
    value: 2,
    label: "2 minutes",
  },
  {
    value: 5,
    label: "5 minutes",
  },
  {
    value: 10,
    label: "10 minutes",
  },
];

interface IControlRoomRefreshSectionProps {
  userSettings: IParsedUserInfo | null;
  onSettingsUpdate?: (settings: Partial<IParsedUserInfo>) => void;
}

const ControlRoomRefreshSection: FC<IControlRoomRefreshSectionProps> = ({
  userSettings,
  onSettingsUpdate,
}) => {
  const { loading: updateUserLoading, updateUserSettings } =
    useUpdateUserSettings();

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedOption, setSelectedOption] = useState<IRefreshOption>(
    refreshOptions[2] // Default to 2 minutes
  );

  useEffect(() => {
    const defaultOption = refreshOptions.find(
      option =>
        option.value ===
        (userSettings?.controlRoomRefreshMinutes ??
          DEFAULT_CONTROL_ROOM_REFRESH_MINUTES)
    );

    setSelectedOption(defaultOption ?? refreshOptions[2]);
  }, [userSettings]);

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);

    setOpen(previousOpen => !previousOpen);
  };

  const handleSelectedOptionChange = (option: IRefreshOption) => {
    setSelectedOption(option);

    const newSettings: ExtendedUserSettingsInput = {
      defaultCameraFov: userSettings?.cameraFov as number,
      defaultWindspeedUnit: userSettings?.windspeedUnit as string,
      defaultVideoSpeed: userSettings?.videoSpeed as number,
      controlRoomRefreshMinutes: option.value,
    };

    updateUserSettings(newSettings)
      .then((response): void => {
        if (response?.data?.updateUserSettings) {
          successNotification("User preferences updated successfully");

          onSettingsUpdate?.({ controlRoomRefreshMinutes: option.value });

          setOpen(false);
        }
      })
      .catch(error => {
        errorNotification(error.message);
      });
  };

  if (updateUserLoading) {
    return <FullPageLoader />;
  }

  return (
    <UserPreferencesSection title="Control room refresh">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5em",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography variant="body2Regular">Auto-refresh interval</Typography>

          <Button
            disableRipple
            sx={{
              ml: "auto",
              padding: 0,
              height: "fit-content",
              color: theme => theme.palette.text.primary,
            }}
            variant="text"
            endIcon={
              open ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />
            }
            onClick={handleButtonClick}
          >
            <Typography variant="body2Regular">
              {selectedOption.label}
            </Typography>
          </Button>

          <Popper open={open} anchorEl={anchorEl} transition placement="right">
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Paper
                  sx={{
                    bgcolor: theme => theme.palette.otherBackgroundLight.main,
                  }}
                >
                  <List>
                    {refreshOptions.map(option => {
                      return (
                        <ControlRoomRefreshOption
                          key={option.label}
                          label={option.label}
                          value={option.value}
                          active={option.value === selectedOption.value}
                          onSelectedOptionChange={handleSelectedOptionChange}
                        />
                      );
                    })}
                  </List>
                </Paper>
              </Fade>
            )}
          </Popper>
        </Box>

        <Typography
          variant="caption"
          sx={{ color: theme => theme.palette.text.secondary }}
        >
          The Control Room page will automatically refresh at the selected
          interval. Select "Disabled" to turn off auto-refresh.
        </Typography>
      </Box>
    </UserPreferencesSection>
  );
};

export default ControlRoomRefreshSection;
